import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut, Inbox, Truck, Droplets, BellDot, Trash2, ChevronDown, Loader2, X,
  Upload, FileCheck2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  nuova: { label: "Nuova", cls: "bg-orange/10 text-orange border-orange/30" },
  in_lavorazione: { label: "In lavorazione", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  completata: { label: "Completata", cls: "bg-green-50 text-green-700 border-green-200" },
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [uploading, setUploading] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, r, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/quote-requests"),
        api.get("/catalogs"),
      ]);
      setStats(s.data);
      setRequests(r.data);
      setCatalogs(c.data);
    } catch {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const uploadCatalog = async (brand, file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Sono ammessi solo file PDF");
      return;
    }
    setUploading(brand);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/catalogs/${brand}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Catalogo ${brand.toUpperCase()} aggiornato`);
      const { data } = await api.get("/catalogs");
      setCatalogs(data);
    } catch (err) {
      toast.error("Caricamento non riuscito");
    } finally {
      setUploading(null);
    }
  };

  const resetCatalog = async (brand) => {
    try {
      await api.delete(`/admin/catalogs/${brand}`);
      toast.success(`Catalogo ${brand.toUpperCase()} ripristinato al segnaposto`);
      const { data } = await api.get("/catalogs");
      setCatalogs(data);
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quote-requests/${id}`, { status });
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      load();
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/quote-requests/${id}`);
      setRequests((rs) => rs.filter((r) => r.id !== id));
      setSelected(null);
      toast.success("Richiesta eliminata");
      load();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const filtered = requests.filter((r) => (filter === "all" ? true : r.category === filter));

  const cards = [
    { label: "Totale richieste", value: stats?.total ?? 0, icon: Inbox },
    { label: "Nuove", value: stats?.nuove ?? 0, icon: BellDot },
    { label: "Gasolio", value: stats?.gasolio ?? 0, icon: Truck },
    { label: "Olii", value: stats?.olii ?? 0, icon: Droplets },
    { label: "AdBlue", value: stats?.adblue ?? 0, icon: Droplets },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="bg-navy text-white sticky top-0 z-30">
        <div className="max-w-8xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="inline-flex bg-white px-2.5 py-1.5 rounded-sm">
              <img src="/logo-mp.png" alt="Martegiani Petroli Srl" className="h-7 w-auto" />
            </div>
            <span className="hidden sm:block font-display text-base tracking-tight">Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-white/60">{user?.name || user?.username}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-2 text-sm border border-white/20 px-4 py-2 hover:bg-orange hover:border-orange transition-colors">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-8xl mx-auto px-6 md:px-10 py-10">
        <h1 className="font-display text-navy text-3xl md:text-4xl mb-8">Richieste di Preventivo</h1>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-navy/10 border border-navy/10 mb-10">
          {cards.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white p-6" data-testid={`stat-${i}`}
            >
              <c.icon className="w-5 h-5 text-orange" />
              <p className="font-display text-4xl text-navy mt-4">{c.value}</p>
              <p className="text-[11px] uppercase tracking-[0.15em] text-ink-soft mt-1">{c.label}</p>
            </motion.div>
          ))}
        </div>

        <section className="mb-12" data-testid="catalog-manager">
          <h2 className="font-display text-navy text-2xl mb-1">Gestione Cataloghi Olii</h2>
          <p className="text-ink-soft text-sm mb-5">Carica il PDF per ogni marca. Sostituisce automaticamente il catalogo mostrato sul sito.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-navy/10 border border-navy/10">
            {catalogs.map((c) => (
              <div key={c.brand} className="bg-white p-6 flex flex-col gap-4" data-testid={`catalog-row-${c.brand}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display text-2xl text-navy uppercase">{c.brand}</span>
                  {c.custom ? (
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-green-700">
                      <FileCheck2 className="w-4 h-4" /> Caricato
                    </span>
                  ) : (
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-soft">Segnaposto</span>
                  )}
                </div>
                <p className="text-xs text-ink-soft truncate">{c.filename}</p>
                <div className="flex items-center gap-2 mt-auto">
                  <label className="flex-1 cursor-pointer flex items-center justify-center gap-2 bg-navy text-white py-2.5 text-xs font-semibold uppercase tracking-[0.12em] hover:bg-orange transition-colors" data-testid={`upload-label-${c.brand}`}>
                    {uploading === c.brand ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {c.custom ? "Sostituisci" : "Carica PDF"}
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      data-testid={`upload-input-${c.brand}`}
                      disabled={uploading === c.brand}
                      onChange={(e) => uploadCatalog(c.brand, e.target.files?.[0])}
                    />
                  </label>
                  <a
                    href={c.custom ? `${API}${c.url.replace(/^\/api/, "")}` : c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`catalog-view-${c.brand}`}
                    className="p-2.5 border border-navy/15 text-navy hover:border-navy transition-colors"
                    aria-label="Anteprima"
                  >
                    <FileCheck2 className="w-4 h-4" />
                  </a>
                  {c.custom && (
                    <button
                      onClick={() => resetCatalog(c.brand)}
                      data-testid={`catalog-reset-${c.brand}`}
                      className="p-2.5 border border-navy/15 text-ink-soft hover:text-red-600 hover:border-red-300 transition-colors"
                      aria-label="Ripristina segnaposto"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="flex gap-2 mb-5">
          {[
            { k: "all", l: "Tutte" },
            { k: "gasolio", l: "Gasolio" },
            { k: "olii", l: "Olii" },
            { k: "adblue", l: "AdBlue" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`px-4 py-2 text-sm font-semibold uppercase tracking-[0.1em] border transition-colors ${
                filter === f.k ? "bg-navy text-white border-navy" : "bg-white text-navy border-navy/15 hover:border-navy"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid place-items-center py-24">
            <Loader2 className="w-8 h-8 text-orange animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-navy/10 py-20 text-center text-ink-soft" data-testid="empty-state">
            <Inbox className="w-10 h-10 mx-auto text-navy/30" />
            <p className="mt-4">Nessuna richiesta presente.</p>
          </div>
        ) : (
          <div className="bg-white border border-navy/10 overflow-x-auto" data-testid="requests-table">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="border-b border-navy/10 text-left text-[11px] uppercase tracking-[0.15em] text-ink-soft">
                  <th className="p-4 font-semibold">Data</th>
                  <th className="p-4 font-semibold">Rif.</th>
                  <th className="p-4 font-semibold">Tipologia</th>
                  <th className="p-4 font-semibold">Ragione Sociale</th>
                  <th className="p-4 font-semibold">Richiedente</th>
                  <th className="p-4 font-semibold">Contatti</th>
                  <th className="p-4 font-semibold">Stato</th>
                  <th className="p-4 font-semibold text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-navy/5 hover:bg-[var(--bg)] cursor-pointer" onClick={() => setSelected(r)} data-testid={`req-row-${r.id}`}>
                    <td className="p-4 text-ink-soft whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="p-4 font-mono text-xs text-orange whitespace-nowrap">{r.reference || "—"}</td>
                    <td className="p-4 font-medium text-navy">{r.tipo}</td>
                    <td className="p-4">{r.ragione_sociale}</td>
                    <td className="p-4 text-ink-soft">{r.nome_richiedente}</td>
                    <td className="p-4 text-ink-soft">
                      <div>{r.telefono}</div>
                      <div className="text-xs">{r.email}</div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-block px-2.5 py-1 text-[11px] font-semibold border ${STATUS[r.status]?.cls}`}>
                        {STATUS[r.status]?.label || r.status}
                      </span>
                    </td>
                    <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-2">
                        <div className="relative">
                          <select
                            value={r.status}
                            onChange={(e) => updateStatus(r.id, e.target.value)}
                            data-testid={`status-select-${r.id}`}
                            className="appearance-none border border-navy/15 bg-white pl-3 pr-8 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-orange"
                          >
                            <option value="nuova">Nuova</option>
                            <option value="in_lavorazione">In lavorazione</option>
                            <option value="completata">Completata</option>
                          </select>
                          <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-ink-soft" />
                        </div>
                        <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} aria-label="Elimina" className="p-2 text-ink-soft hover:text-red-600 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selected && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="detail-drawer">
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.4 }}
            className="relative w-full max-w-md bg-white h-full overflow-y-auto"
          >
            <div className="bg-navy text-white p-6 flex items-start justify-between sticky top-0">
              <div>
                <span className="text-orange text-[11px] uppercase tracking-[0.25em]">{selected.category}</span>
                <h3 className="font-display text-2xl mt-1">{selected.tipo}</h3>
              </div>
              <button onClick={() => setSelected(null)} data-testid="drawer-close" className="text-white/70 hover:text-white"><X /></button>
            </div>
            <div className="p-6 space-y-1">
              {[
                ["Riferimento", selected.reference],
                ["Ragione Sociale", selected.ragione_sociale],
                ["Partita IVA", selected.partita_iva],
                ["Codice Fiscale", selected.codice_fiscale],
                ["Richiedente", selected.nome_richiedente],
                ["Telefono", selected.telefono],
                ["Email", selected.email],
                ["Sede serbatoio da rifornire", selected.sede_serbatoio],
                ["Quantità / Interesse", selected.quantita],
                ["Messaggio", selected.messaggio],
                ["Data richiesta", new Date(selected.created_at).toLocaleString("it-IT")],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-3 border-b border-navy/5">
                  <span className="text-[11px] uppercase tracking-[0.12em] text-ink-soft shrink-0">{k}</span>
                  <span className="text-navy font-medium text-right break-all">{v || "—"}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
